import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AntLocalization from '../../utils/AntLocalization';
import AntAPI from '../../utils/AntAPI';
import AntConfig from '../../utils/AntConfig';
import AntCondition from '../../utils/AntCondition';
import AntTime from '../../utils/AntTime';
import { AntGlobal, AntSession } from '../../utils/AntContext';
import './BrowsePanel.scss';

// Walk a `when` object tree and collect all field names referenced
// (top-level keys excluding the nested `group*: { when: {} }` wrappers and `whenType`)
function collectWhenFields(when, out) {
    if (!when || typeof when !== 'object') return;
    Object.keys(when).forEach(function (k) {
        if (k === 'whenType') return;
        var v = when[k];
        if (v && typeof v === 'object' && v.when && typeof v.when === 'object') {
            collectWhenFields(v.when, out);
        } else {
            out[k] = true;
        }
    });
}

function pickRowStyle(rowStyles, row) {
    if (!(rowStyles instanceof Array)) return null;
    for (var i = 0; i < rowStyles.length; i++) {
        var rs = rowStyles[i];
        if (!rs || !rs.when || typeof rs.when !== 'object') continue;
        try {
            if (AntCondition.isValid(row, { when: rs.when, whenType: rs.whenType })) return rs;
        } catch (e) {
            /* skip */
        }
    }
    return null;
}

var PAGE_SIZE = 30;
var DEBOUNCE_MS = 300;
var AUTO_RELOAD_MS = 60000; // periodic silent reload
var ACTIVITY_QUIET_MS = 5000; // skip auto-reload if user was active within this window

// ─── i18n labels (10 languages per ANTE convention) ─────────────
var L = {
    searchPlaceholder: {
        vi: 'Tìm phiếu...',
        en: 'Search vouchers...',
        fr: 'Rechercher...',
        es: 'Buscar...',
        id: 'Cari...',
        ja: '検索...',
        zh: '搜索...',
        ko: '검색...',
        hi: 'खोजें...',
        ar: 'بحث...'
    },
    loading: {
        vi: 'Đang tải...',
        en: 'Loading...',
        fr: 'Chargement...',
        es: 'Cargando...',
        id: 'Memuat...',
        ja: '読み込み中...',
        zh: '加载中...',
        ko: '불러오는 중...',
        hi: 'लोड हो रहा है...',
        ar: 'جار التحميل...'
    },
    empty: {
        vi: 'Không có phiếu',
        en: 'No vouchers',
        fr: 'Aucun bon',
        es: 'Sin vales',
        id: 'Tidak ada',
        ja: 'データなし',
        zh: '无数据',
        ko: '데이터 없음',
        hi: 'कोई दस्तावेज़ नहीं',
        ar: 'لا توجد'
    },
    end: { vi: 'hết', en: 'end', fr: 'fin', es: 'fin', id: 'akhir', ja: '終了', zh: '结束', ko: '끝', hi: 'अंत', ar: 'النهاية' },
    today: { vi: 'Hôm nay', en: 'Today', fr: "Aujourd'hui", es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم' },
    all: { vi: 'Tất cả', en: 'All', fr: 'Tous', es: 'Todos', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' },
    editorNotFound: {
        vi: 'Không tìm thấy editor: ',
        en: 'Editor not found: ',
        fr: 'Éditeur introuvable: ',
        es: 'Editor no encontrado: ',
        id: 'Editor tidak ditemukan: ',
        ja: 'エディタが見つかりません: ',
        zh: '未找到编辑器: ',
        ko: '에디터를 찾을 수 없음: ',
        hi: 'एडिटर नहीं मिला: ',
        ar: 'لم يتم العثور على المحرر: '
    },
    editorMissing: {
        vi: 'Editor thiếu module/table',
        en: 'Editor missing module/table',
        fr: 'Éditeur sans module/table',
        es: 'Editor sin módulo/tabla',
        id: 'Editor tanpa modul/tabel',
        ja: 'モジュール/テーブル未設定',
        zh: '编辑器缺少模块/表',
        ko: '모듈/テーブル 누락',
        hi: 'मॉड्यूल/टेबल अनुपस्थित',
        ar: 'الوحدة/الجدول مفقود'
    }
};

// ─── Serve place type prefix (10 languages, sourced from ServePlace.type) ─
var PLACE_TYPE_PREFIX = {
    Table: { vi: 'Bàn', en: 'Table', fr: 'Table', es: 'Mesa', id: 'Meja', ja: 'テーブル', zh: '桌', ko: '테이블', hi: 'टेबल', ar: 'طاولة' },
    Room: { vi: 'Phòng', en: 'Room', fr: 'Salle', es: 'Sala', id: 'Ruang', ja: '部屋', zh: '房间', ko: '룸', hi: 'कक्ष', ar: 'قاعة' },
    Seat: { vi: 'Ghế', en: 'Seat', fr: 'Siège', es: 'Asiento', id: 'Kursi', ja: '席', zh: '座位', ko: '좌석', hi: 'सीट', ar: 'مقعد' },
    Field: { vi: 'Sân', en: 'Field', fr: 'Terrain', es: 'Cancha', id: 'Lapangan', ja: 'コート', zh: '场地', ko: '구장', hi: 'मैदान', ar: 'ملعب' }
};

function applyPlacePrefix(placeName, placeType) {
    if (!placeName) return '';
    var spec = placeType && PLACE_TYPE_PREFIX[placeType];
    if (!spec) return placeName;
    var prefix = AntLocalization.translateText(spec);
    if (!prefix) return placeName;
    // Avoid double prefix when name already starts with the prefix word
    var lower = String(placeName).toLowerCase();
    if (lower.indexOf(prefix.toLowerCase()) === 0) return placeName;
    return prefix + ' ' + placeName;
}

// ─── Field auto-detection ───────────────────────────────────────
function detectFields(editor) {
    var lookup = (editor && editor.lookup) || {};
    var valueField = lookup.valueField || 'code';
    var displayField = lookup.displayField;

    var columns = lookup.columns || [];
    var colNames = columns
        .map(function (c) {
            return typeof c === 'string' ? c : c && c.dataField;
        })
        .filter(Boolean);

    // Fallback: editor cấu hình cho antSelectBox (vd ACUserGroups_All) thường chỉ có
    // valueField + displayField, KHÔNG có columns. Thêm displayField vào colNames để
    // pick() dưới đây có data — tránh row hiển thị trống.
    if (colNames.length === 0 && displayField && colNames.indexOf(displayField) < 0) {
        colNames.push(displayField);
    }

    function pick(candidates, regex) {
        for (var i = 0; i < candidates.length; i++) {
            if (colNames.indexOf(candidates[i]) >= 0) return candidates[i];
        }
        if (regex) {
            var found = colNames.find(function (n) {
                return regex.test(n);
            });
            if (found) return found;
        }
        return null;
    }

    var place = pick(['serve_place_name', 'place_name', 'table_name', 'room_name', 'court_name']);
    // Infer companion *_type field even when it's not in the editor's columns —
    // we'll still pull it into selectFields so the prefix (Bàn/Phòng/…) can render.
    var placeType = pick(['serve_place_type', 'place_type']);
    if (!placeType && place) {
        if (place === 'serve_place_name') placeType = 'serve_place_type';
        else if (place === 'place_name') placeType = 'place_type';
    }

    return {
        code: valueField,
        place: place,
        placeType: placeType,
        date: pick([
            'appointment_date',
            'booking_start_date',
            'released_at',
            'created_at',
            'order_date',
            'doc_date',
            'statement_date',
            'invoice_date',
            'transfer_date'
        ]),
        endDate: pick(['booking_end_date', 'order_end_date', 'end_date']),
        entity: pick(['entity_name', 'customer_name', 'supplier_name', 'vendor_name', 'partner_name', 'name']) || displayField || null,
        description: pick(['description', 'note', 'memo']),
        amount: pick(['total_gross_price', 'total_net_price', 'total_amt', 'gross_amt', 'amt', 'total_onhand_amt', 'total_available_amt']),
        status: pick(['mix_status'], /_status$/),
        currency: 'currency_code'
    };
}

// ─── Formatters (ANTE convention + time for browsing context) ────
// Date only — no time. Same year → DD/MM; other → DD/MM/YY
function formatDate(unixSec) {
    if (!unixSec || isNaN(unixSec)) return '';
    var n = Number(unixSec);
    if (AntTime.isUnixWithinCurrentYear(n)) return AntTime.fromUnix(n).format('DD/MM');
    return AntTime.fromUnix(n).format('DD/MM/YY');
}

// Currency per row currency_code (fallback Sys_BaseCurrency)
function formatAmount(value, row) {
    if (value === null || value === undefined || value === '' || isNaN(value)) return '';
    var currency = AntLocalization.resolveCurrency(null, row, null, 'currency_code');
    return AntLocalization.translateCurrency(Number(value), false, { currency: currency });
}

// ─── Status translation from field config ──────────────────────
// AntConfig stores enum values at cnf.lookup.values (NOT cnf.values)
function translateStatus(antConfig, table, statusField, value) {
    if (!value) return '';
    if (!antConfig || !table || !statusField) return String(value);
    try {
        var cnf = antConfig.getCnfField(statusField, table);
        var values = cnf && cnf.lookup && cnf.lookup.values;
        if (values instanceof Array) {
            var match = values.find(function (v) {
                return v.code === value;
            });
            if (match && match.name) return AntLocalization.translateText(match.name);
        }
    } catch (e) {
        /* silent */
    }
    return String(value);
}

// ─── Main component ─────────────────────────────────────────────
var BrowsePanel = forwardRef(function BrowsePanel(props, ref) {
    var appGlobal = AntGlobal();
    var session = AntSession ? AntSession() : null;
    var navigate = useNavigate();
    var routeParams = useParams();
    var location = useLocation();
    var config = props.config || {};
    var editorName = config.editorName;
    var customRender = typeof config.customRender === 'function' ? config.customRender : null;
    var rowStyles = config.rowStyles instanceof Array ? config.rowStyles : null;

    // Resolve editor + AntConfig
    var antConfig = useMemo(
        function () {
            if (!appGlobal) return null;
            return new AntConfig(appGlobal, '');
        },
        [appGlobal]
    );

    var editor = useMemo(
        function () {
            if (!editorName || !antConfig) return null;
            return antConfig.getCnfEditor(editorName);
        },
        [antConfig, editorName]
    );

    var lookup = (editor && editor.lookup) || {};
    // Resolve module: ưu tiên lookup.module nếu có; fallback derive từ lookup.url
    // (đa số editor configs có 'module/Table/list' format, không khai báo module riêng).
    var module = lookup.module;
    if (!module && lookup.url) {
        var _u = String(lookup.url).replace(/^\/+/, '');
        var _seg = _u.split('/');
        if (_seg.length > 0 && _seg[0]) module = _seg[0];
    }
    var table = lookup.table;
    var moduleTableConfig = useMemo(
        function () {
            if (!appGlobal || !table) return null;
            return new AntConfig(appGlobal, table);
        },
        [appGlobal, table]
    );

    var fields = useMemo(
        function () {
            return detectFields(editor);
        },
        [editor]
    );

    var api = useMemo(
        function () {
            if (!module || !table) return null;
            return new AntAPI(module, table);
        },
        [module, table]
    );

    // State
    var [searchText, setSearchText] = useState('');
    var [debouncedText, setDebouncedText] = useState('');
    var [activeFilters, setActiveFilters] = useState({});
    var [filterCategories, setFilterCategories] = useState([]);
    var [rows, setRows] = useState([]);
    var [loading, setLoading] = useState(false);
    var [hasMore, setHasMore] = useState(true);
    var [pageIndex, setPageIndex] = useState(0);
    var listRef = useRef(null);
    var reqIdRef = useRef(0);
    var filterReqIdRef = useRef(0);
    var lastActivityRef = useRef(0); // updated on typing/scrolling
    var loadOptsRef = useRef(null); // latest loadData closure for use in timer/imperative API

    // Reset active filters whenever editor changes
    useEffect(
        function () {
            setActiveFilters({});
        },
        [editorName]
    );

    // Load filter categories dynamically from editor.searchCategories1 via /search/prepare.
    // Mirrors AntGridCategories — BE returns only values present in data (filtered by editor.searchConditions).
    useEffect(
        function () {
            var sc = lookup.searchCategories1;
            if (!api || !sc || !(sc.lookups instanceof Array) || sc.lookups.length === 0) {
                setFilterCategories([]);
                return;
            }
            var selectLookups = sc.lookups.map(function (c) {
                return c.name;
            });
            var prepParams = Object.assign({}, lookup.searchConditions || {});
            sc.lookups.forEach(function (k) {
                delete prepParams[k.field];
            });
            var reqId = ++filterReqIdRef.current;
            api.postSearchPrepare(prepParams, { selectLookups: selectLookups, caching: sc.caching, freshness: sc.freshness })
                .then(function (result) {
                    if (reqId !== filterReqIdRef.current) return;
                    var out = [];
                    var resultLookups = (result && result.lookups) || [];
                    resultLookups.forEach(function (l) {
                        var spec = sc.lookups.find(function (c) {
                            return c.name === l.name;
                        });
                        if (!spec) return;
                        var fieldName = spec.field || spec.name;
                        var values = (l.values || [])
                            .map(function (v) {
                                if (typeof v === 'object' && v !== null) {
                                    return { code: v.code !== undefined ? v.code : v.value, name: v.name };
                                }
                                return { code: v, name: { vi: String(v), en: String(v) } };
                            })
                            .filter(function (v) {
                                return v.code !== undefined && v.code !== null && v.code !== '';
                            });
                        if (sc.excludes instanceof Array) {
                            values = values.filter(function (v) {
                                return sc.excludes.indexOf(v.code) < 0;
                            });
                        }
                        if (values.length > 0) out.push({ field: fieldName, values: values, multiSelect: !!sc.multiSelect });
                    });
                    setFilterCategories(out);
                })
                .catch(function () {
                    if (reqId !== filterReqIdRef.current) return;
                    setFilterCategories([]);
                });
        },
        [api, editorName, lookup.searchCategories1, lookup.searchConditions]
    );

    // Merge editor.lookup.searchConditions with activeFilters (filter chips take precedence)
    var resolvedSearchConds = useMemo(
        function () {
            var base = lookup.searchConditions || {};
            return Object.assign({}, base, activeFilters);
        },
        [lookup.searchConditions, activeFilters]
    );

    useEffect(
        function () {
            var t = setTimeout(function () {
                setDebouncedText(searchText);
            }, DEBOUNCE_MS);
            return function () {
                clearTimeout(t);
            };
        },
        [searchText]
    );

    var selectFields = useMemo(
        function () {
            var s = ['id', 'checksum'];
            if (fields.code) s.push(fields.code);
            if (fields.place) s.push(fields.place);
            if (fields.placeType) s.push(fields.placeType);
            if (fields.date) s.push(fields.date);
            if (fields.endDate) s.push(fields.endDate);
            if (fields.entity) s.push(fields.entity);
            if (fields.description) s.push(fields.description);
            if (fields.amount) s.push(fields.amount);
            if (fields.status) s.push(fields.status);
            s.push('currency_code');
            // Pull in any field referenced by rowStyles conditions so coloring evaluates correctly
            if (rowStyles) {
                var bag = {};
                rowStyles.forEach(function (rs) {
                    if (rs && rs.when) collectWhenFields(rs.when, bag);
                });
                Object.keys(bag).forEach(function (k) {
                    s.push(k);
                });
            }
            return Array.from(new Set(s.filter(Boolean)));
        },
        [fields, rowStyles]
    );

    var loadData = useCallback(
        function (reset, nextPageIndex, opts) {
            if (!api) return;
            var silent = !!(opts && opts.silent);
            var currentPage = reset ? 0 : nextPageIndex !== undefined ? nextPageIndex : pageIndex;
            var reqId = ++reqIdRef.current;
            if (!silent) setLoading(true);

            var apiParams = Object.assign({}, resolvedSearchConds, {
                searchText: debouncedText || undefined,
                ordering: lookup.ordering || [{ name: 'created_at', type: 'desc' }],
                pageSize: PAGE_SIZE,
                pageIndex: currentPage,
                selectFields: selectFields
            });

            var url = lookup.url ? (String(lookup.url).startsWith('/') ? '' : '/') + lookup.url : null;

            api.getList(apiParams, url, { byPassDefaultConds: true })
                .then(function (result) {
                    if (reqId !== reqIdRef.current) return; // stale
                    var newRows = (result && result.rows) || [];
                    if (reset) setRows(newRows);
                    else
                        setRows(function (prev) {
                            return prev.concat(newRows);
                        });
                    setHasMore(newRows.length === PAGE_SIZE);
                    if (!silent) setLoading(false);
                })
                .catch(function () {
                    if (reqId !== reqIdRef.current) return;
                    if (!silent) setLoading(false);
                    setHasMore(false);
                });
        },
        [api, resolvedSearchConds, lookup.ordering, lookup.url, debouncedText, pageIndex, selectFields]
    );

    // Keep latest loadData closure in ref for use inside setInterval / imperative API
    loadOptsRef.current = loadData;

    // Reset & reload when search text, editor, or filter chips change
    useEffect(
        function () {
            setPageIndex(0);
            setRows([]);
            setHasMore(true);
            loadData(true, 0);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [debouncedText, editorName, activeFilters]
    );

    // Track user typing as activity (delays auto-reload)
    useEffect(
        function () {
            lastActivityRef.current = Date.now();
        },
        [searchText, activeFilters]
    );

    // Periodic silent reload — refresh page 0 quietly while user is idle.
    useEffect(
        function () {
            if (!api) return undefined;
            var timerId = setInterval(function () {
                var now = Date.now();
                if (now - lastActivityRef.current < ACTIVITY_QUIET_MS) return;
                var fn = loadOptsRef.current;
                if (typeof fn !== 'function') return;
                fn(true, 0, { silent: true });
            }, AUTO_RELOAD_MS);
            return function () {
                clearInterval(timerId);
            };
        },
        [api, editorName]
    );

    // ─── Imperative API ─────────────────────────────────────────
    // refreshRow(code, partial): merge `partial` into the row whose code matches.
    //   Used by AntVoucher after actionUpdatePrepare to update only the current row
    //   without reloading the whole list.
    // reload(): force a silent reload of page 0.
    useImperativeHandle(
        ref,
        function () {
            return {
                refreshRow: function (code, partial) {
                    if (code === undefined || code === null || code === '') return;
                    if (!partial || typeof partial !== 'object') return;
                    var codeField = fields.code || 'code';
                    setRows(function (prev) {
                        var changed = false;
                        var next = prev.map(function (r) {
                            if (String(r[codeField]) === String(code)) {
                                changed = true;
                                return Object.assign({}, r, partial);
                            }
                            return r;
                        });
                        return changed ? next : prev;
                    });
                },
                reload: function () {
                    var fn = loadOptsRef.current;
                    if (typeof fn === 'function') fn(true, 0, { silent: true });
                }
            };
        },
        [fields.code]
    );

    // Load more when pageIndex advances (> 0)
    useEffect(
        function () {
            if (pageIndex > 0) loadData(false, pageIndex);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [pageIndex]
    );

    var handleScroll = useCallback(
        function (e) {
            lastActivityRef.current = Date.now();
            if (loading || !hasMore) return;
            var el = e.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
                setPageIndex(function (p) {
                    return p + 1;
                });
            }
        },
        [loading, hasMore]
    );

    var handleSelect = useCallback(
        function (record) {
            if (!record || !module || !table) return;
            var code = record.code !== undefined ? record.code : record[fields.code];
            if (code === undefined || code === null || code === '') return;
            // Thanh duyệt nằm trong trang chi tiết /<nhóm>/<Màn>/:mã — giữ nguyên trang đang mở, chỉ thay mã
            // (như ô chuyển phiếu ở header). Ghép theo bảng của editor (/exp/EXPOrder/…) sẽ mở phiếu Mua hàng,
            // Bảng lương… ở màn Chi tiêu chung, phiếu Phải thu khác ở màn Bán hàng.
            var current = String((location && location.pathname) || '');
            var segs = current.split('/').filter(Boolean);
            var currentCode = routeParams && (routeParams.code || routeParams.id);
            if (segs.length >= 3 && currentCode && decodeURIComponent(segs[segs.length - 1]) === String(currentCode)) {
                segs[segs.length - 1] = encodeURIComponent(code);
                navigate('/' + segs.join('/'));
                return;
            }
            navigate('/' + module + '/' + table + '/' + encodeURIComponent(code));
        },
        [navigate, module, table, fields.code, location, routeParams]
    );

    var handleClearSearch = useCallback(function () {
        setSearchText('');
    }, []);

    // Single-select per chip — click toggles on/off; clicking another value replaces.
    var handleToggleFilter = useCallback(function (cat, value) {
        setActiveFilters(function (prev) {
            var next = Object.assign({}, prev);
            if (next[cat.field] === value) delete next[cat.field];
            else next[cat.field] = value;
            return next;
        });
    }, []);

    var isFilterActive = useCallback(
        function (field, value) {
            return activeFilters[field] === value;
        },
        [activeFilters]
    );

    var currentCode = routeParams && routeParams.code ? decodeURIComponent(String(routeParams.code)) : null;

    // ─── Empty / error states ────────────────────────────────────
    if (!editorName) return null;
    if (!editor) {
        return (
            <div className="ls-browse-empty">
                {AntLocalization.translateText(L.editorNotFound)}
                <b>{editorName}</b>
            </div>
        );
    }
    if (!module || !table) {
        return <div className="ls-browse-empty">{AntLocalization.translateText(L.editorMissing)}</div>;
    }

    // ─── Render ──────────────────────────────────────────────────
    return (
        <div className="ls-browse-panel">
            <div className="ls-browse-search">
                <svg
                    className="ls-browse-search-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    type="text"
                    className="ls-browse-search-input"
                    placeholder={AntLocalization.translateText(L.searchPlaceholder)}
                    value={searchText}
                    onChange={function (e) {
                        setSearchText(e.target.value);
                    }}
                />
                {searchText && (
                    <button className="ls-browse-search-clear" onClick={handleClearSearch} type="button">
                        ×
                    </button>
                )}
            </div>

            {filterCategories.length > 0 &&
                filterCategories.map(function (cat) {
                    return (
                        <div key={cat.field} className="ls-browse-filters">
                            {cat.values.map(function (v) {
                                var active = isFilterActive(cat.field, v.code);
                                var statusClass = String(v.code || '').replace(/[^a-zA-Z0-9_-]/g, '');
                                return (
                                    <button
                                        key={v.code}
                                        type="button"
                                        className={'ls-browse-chip status-' + statusClass + (active ? ' active' : '')}
                                        onClick={function () {
                                            handleToggleFilter(cat, v.code);
                                        }}
                                    >
                                        <span className="ls-browse-chip-dot" />
                                        {AntLocalization.translateText(v.name)}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}

            <div className="ls-browse-list" ref={listRef} onScroll={handleScroll}>
                {rows.map(function (record, idx) {
                    var code = record[fields.code];
                    var isActive = currentCode !== null && code !== undefined && String(currentCode) === String(code);
                    var key = (code !== undefined ? String(code) : '__row__') + '_' + idx;
                    var matchedStyle = rowStyles && !isActive ? pickRowStyle(rowStyles, record) : null;
                    var inlineStyle = matchedStyle
                        ? {
                              backgroundColor: matchedStyle.backgroundColor,
                              color: matchedStyle.textColor,
                              fontWeight: matchedStyle.fontWeight
                          }
                        : undefined;
                    var itemClass =
                        'ls-browse-item' +
                        (isActive ? ' active' : '') +
                        (matchedStyle ? ' has-row-style' : '') +
                        (matchedStyle && matchedStyle.className ? ' ' + matchedStyle.className : '');

                    if (customRender) {
                        return (
                            <div
                                key={key}
                                className={itemClass}
                                style={inlineStyle}
                                onClick={function () {
                                    handleSelect(record);
                                }}
                            >
                                {customRender(record, { isActive: isActive, fields: fields })}
                            </div>
                        );
                    }

                    var placeRaw = fields.place ? record[fields.place] : '';
                    var placeTypeVal = fields.placeType ? record[fields.placeType] : '';
                    var placeText = applyPlacePrefix(placeRaw, placeTypeVal);
                    var entityText = fields.entity ? record[fields.entity] : '';
                    var descText = fields.description ? record[fields.description] : '';
                    var dateStart = fields.date ? formatDate(record[fields.date]) : '';
                    var dateEnd = fields.endDate ? formatDate(record[fields.endDate]) : '';
                    var dateText = dateStart;
                    if (dateStart && dateEnd && dateEnd !== dateStart) {
                        dateText = dateStart + ' → ' + dateEnd;
                    } else if (!dateStart && dateEnd) {
                        dateText = dateEnd;
                    }
                    var amountValue = fields.amount ? Number(record[fields.amount]) : 0;
                    var amountText = amountValue ? formatAmount(amountValue, record) : '';
                    var statusValue = fields.status ? record[fields.status] : '';
                    var statusText = translateStatus(moduleTableConfig, table, fields.status, statusValue);
                    var statusClass = (statusValue || '').replace(/[^a-zA-Z0-9_-]/g, '');

                    // Title priority: place → entity → description.
                    // Code đã được ẩn khỏi meta row (xem JSX bên dưới) — KHÔNG fallback lên title nếu sau này bật lại.
                    var titleText, subtitleText, titleIsPlace;
                    if (placeText) {
                        titleText = placeText;
                        subtitleText = entityText || descText;
                        titleIsPlace = true;
                    } else if (entityText) {
                        titleText = entityText;
                        subtitleText = descText;
                        titleIsPlace = false;
                    } else if (descText) {
                        titleText = descText;
                        subtitleText = '';
                        titleIsPlace = false;
                    } else {
                        titleText = '';
                        subtitleText = '';
                        titleIsPlace = false;
                    }
                    // Mã code đã được ẩn theo yêu cầu — giữ biến để có thể bật lại sau này.
                    // eslint-disable-next-line no-unused-vars
                    var showCodeInMeta = code !== undefined && code !== null && code !== '';

                    return (
                        <div
                            key={key}
                            className={itemClass}
                            style={inlineStyle}
                            onClick={function () {
                                handleSelect(record);
                            }}
                        >
                            <div className="ls-browse-body">
                                <div className="ls-browse-top">
                                    <div className="ls-browse-main">
                                        {titleText && (
                                            <div
                                                className={'ls-browse-title' + (titleIsPlace ? ' is-place' : '')}
                                                title={titleText}
                                            >
                                                {titleText}
                                            </div>
                                        )}
                                        {subtitleText && (
                                            <div className="ls-browse-subtitle" title={subtitleText}>
                                                {subtitleText}
                                            </div>
                                        )}
                                    </div>
                                    {amountText && <div className="ls-browse-amount">{amountText}</div>}
                                </div>
                                {(statusText || dateText) && (
                                    <div className="ls-browse-meta">
                                        <span className="ls-browse-meta-cell ls-browse-date">{dateText || ''}</span>
                                        <span className={'ls-browse-meta-cell ls-browse-status status-' + statusClass}>
                                            {statusText || ''}
                                        </span>
                                        {/* Mã code đã được ẩn theo yêu cầu — uncomment để bật lại
                                        <span className="ls-browse-meta-cell ls-browse-code">
                                            {showCodeInMeta ? '#' + code : ''}
                                        </span>
                                        */}
                                    </div>
                                )}
                            </div>
                            {/* Chevron đã được ẩn theo yêu cầu — uncomment để bật lại
                            <svg
                                className="ls-browse-chevron"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                            */}
                        </div>
                    );
                })}

                {loading && <div className="ls-browse-loading">{AntLocalization.translateText(L.loading)}</div>}
                {!loading && rows.length === 0 && <div className="ls-browse-empty">{AntLocalization.translateText(L.empty)}</div>}
            </div>
        </div>
    );
});

export default BrowsePanel;
